import {
  Box,
  Button,
  Callout,
  HighlightIcon,
  Icon,
  Skeleton,
} from '@dev-spendesk/grapes';
import classnames from 'classnames';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Link as ReactLink } from 'react-router-dom';

import { CardPayment } from 'common/components/CardPayment/CardPayment';
import { useGoTo } from 'common/hooks/useGoTo';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { FOOTER_ID } from 'modules/bookkeep/payables/components/PayablePanel/PayablePanel';
import type { Payable } from 'modules/bookkeep/payables/containers/PayablePanelContainer';
import { useGetInvoiceCardDetails } from 'modules/cards/hooks/usetGetInvoiceCardFromPayable';
import { SendInvoiceCardBackToReview } from 'modules/single-use-cards/components/SingleUseCardsPanel/SendInvoiceCardBackToReview';
import { routeFor, routes } from 'src/core/constants/routes';
import { getIsInvoiceToCardEnabled } from 'src/core/selectors/globalSelectors';
import { formatMoney } from 'src/core/utils/money';

type Props = {
  payable: Payable;
};

export const InvoiceCardSection = ({ payable }: Props) => {
  const { t } = useTranslation('global');
  const goTo = useGoTo();

  const companyId = useCompanyId();
  const isInvoiceToCardFeatureEnabled = useSelector(getIsInvoiceToCardEnabled);
  const footerElement = document.getElementById(FOOTER_ID);

  const getInvoiceCardDetails = useGetInvoiceCardDetails({
    payableId: payable.id,
    companyId,
    isEnabled:
      isInvoiceToCardFeatureEnabled && payable.type === 'invoicePurchase',
  });

  if (!isInvoiceToCardFeatureEnabled) {
    return null;
  }

  if (getInvoiceCardDetails.isLoading) {
    return (
      <div className="relative top-[-6px] mb-[-8px] h-0 w-full overflow-visible">
        <Skeleton width="100%" height="8px" />
      </div>
    );
  }

  if (getInvoiceCardDetails.isError) {
    return (
      <Callout
        variant="warning"
        title={t('cards.errorLoadingInvoiceDetails')}
        className="flex items-center"
      >
        <Button
          variant="secondaryWarning"
          text={t('lazyLoading.error.retry')}
          onClick={() => getInvoiceCardDetails.refetch}
        />
      </Callout>
    );
  }

  const result = getInvoiceCardDetails.data;
  if (
    !result?.requestId ||
    !result?.invoiceStatus ||
    !['to_pay', 'partially_paid', 'paid'].includes(result.invoiceStatus)
  ) {
    return <></>;
  }

  const goToPayment = (paymentId: string) => {
    goTo('PAYMENTS_ALL', { company: companyId, id: paymentId });
  };

  const getStatusText = () => {
    switch (result.invoiceStatus) {
      case 'to_pay':
        return t('cards.awaitingCardPayment');
      case 'partially_paid':
        return t('cards.processingCardPayment');
      case 'paid':
        return t('cards.cardPaymentCompleted');
      default:
        return '';
    }
  };

  return (
    <>
      <Box
        variant="secondary"
        className="relative flex min-h-[80px] flex-col justify-center gap-8 py-24"
      >
        <div className="flex items-center">
          <HighlightIcon
            name={
              result.invoiceStatus === 'paid'
                ? 'card-lightning'
                : 'clock-filled'
            }
            size={32}
            variant="blue"
            className="mr-12"
          />
          <div className="title-m">{getStatusText()}</div>
          {result.hasCardAccess && (
            <div className="ml-auto">
              <ReactLink
                to={`${routeFor(routes.SINGLE_USE_CARDS.path, {
                  company: companyId,
                  id: result.requestId,
                })}`}
              >
                <Button
                  iconName="chevron-right"
                  iconPosition="right"
                  text={t('misc.goToCard')}
                  variant="secondaryNeutral"
                />
              </ReactLink>
            </div>
          )}
        </div>

        {result.payments &&
          result.payments?.length > 0 &&
          result.payments.map((payment, index) => {
            return (
              <div
                key={payment.id}
                className={classnames('flex items-center justify-between', {
                  'mt-8': index === 0,
                })}
              >
                <CardPayment
                  primarySlot={formatMoney(payment.amount, payment.currency)}
                  secondarySlot={payment.supplierName}
                  tertiarySlot={<Icon name="chevron-right" />}
                  onClick={() => goToPayment(payment.id)}
                />
              </div>
            );
          })}
      </Box>

      {result.invoiceStatus === 'to_pay' &&
        footerElement &&
        createPortal(
          <SendInvoiceCardBackToReview
            requestId={result.requestId}
            payableId={payable.id}
          />,
          footerElement,
        )}
    </>
  );
};
